import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../../shared/components";
import { useBranding, useBrandLogo } from "../../core/hooks/useBranding";
import { useTheme, THEME_MODES } from "../../core/hooks/useTheme";
import { APP_INFO } from "../../core/constants";
import "./auth.css";

/**
 * The auth frame — one plate, one panel, one footer band, four payloads.
 *
 * Every auth screen renders through this so the white-label constraint holds on
 * all four (auth.md: the centre's mark and name are the screen's whole job) and
 * so the "one frame" promise is real rather than four compositions that drift.
 * The panel body is the only variable and comes in as `children`.
 */

// The one repeating motif in the product's ground, at 4% (§5) — the same
// diagonal lattice every other surface draws (auth.css, W-69). aria-hidden.
const Girih = () => <div className="lms-auth__girih" aria-hidden="true" />;

const SunGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  </svg>
);

const MoonGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5Z" />
  </svg>
);

const AuthScene = ({ line, panelTitle, step, children }) => {
  const { t } = useTranslation();
  const { centreName, logoUrl, logoDarkUrl, poweredBy } = useBranding();
  const { isDark, setThemeMode } = useTheme();
  const logoSrc = useBrandLogo(isDark);

  const displayName = centreName || APP_INFO.NAME;
  const hasLogo = Boolean(logoDarkUrl || logoUrl);
  const monogram = displayName.trim().charAt(0).toUpperCase();

  // The plate already shouts the name; a subtitle that only repeats it is noise.
  // A centre that leaves the subtitle blank (or types its own name into it) gets
  // just the name, not the same words twice.
  const showLine =
    Boolean(line) && line.trim().toLowerCase() !== displayName.trim().toLowerCase();

  const toggleTheme = () =>
    setThemeMode(isDark ? THEME_MODES.LIGHT : THEME_MODES.DARK);

  return (
    <div className="lms-auth">
      <Girih />

      {/* ---- the plate: whose product is this ---- */}
      <div className="lms-auth__plate">
        <div className={`lms-auth__mark${hasLogo ? "" : " lms-auth__mark--tile"}`}>
          {hasLogo ? (
            <img
              src={logoSrc}
              alt={displayName}
              className="lms-auth__mark-img"
              // A centre that uploads a broken or since-deleted logo gets the
              // vendor mark rather than a broken-image icon on its front door.
              onError={(e) => { e.currentTarget.src = "/images/lms.png"; }}
            />
          ) : (
            <span className="lms-auth__mark-monogram" aria-hidden="true">{monogram}</span>
          )}
        </div>
        <h1 className="lms-auth__name">{displayName}</h1>
        {showLine && <p className="lms-auth__line">{line}</p>}
      </div>

      {/* ---- the panel column: panel + footer band ---- */}
      <div className="lms-auth__side">
        <div className="lms-auth__panel">
          <div className="lms-auth__panel-head">
            <h2 className="lms-auth__title">{panelTitle}</h2>
            {step && (
              <span className="lms-auth__steps">
                <b>{step.current}</b>/{step.total}
              </span>
            )}
          </div>
          {children}
        </div>

        <div className="lms-auth__foot">
          <div className="lms-auth__foot-left">
            <LanguageSwitcher size="small" />
            <button type="button" className="lms-auth__chip" onClick={toggleTheme}>
              {isDark ? <MoonGlyph /> : <SunGlyph />}
              {isDark ? t("settings.darkMode") : t("settings.lightMode")}
            </button>
          </div>
          {poweredBy && (
            <span className="lms-auth__by">
              {t("common.poweredBy")}{" "}
              <a href={poweredBy.url} target="_blank" rel="noreferrer">{poweredBy.name}</a>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthScene;
